/**
 * Paystack Payment Service
 * Handles payment initialization and verification for admin bookings
 */

import { getApiBaseUrl } from '@/config/environment';


export interface PaymentInitRequest {
  amount: number;
  email: string;
  channel?: 'card' | 'bank' | 'bank_transfer' | 'ussd' | 'qr' | 'mobile_money';
  metadata: {
    bookingId: string;
    userId: string;
    bookingType: 'admin_created';
    adminId: string;
  };
}

export interface PaymentInitResponse {
  success: boolean;
  data: {
    reference: string;
    authorizationUrl: string;
    accessCode: string;
    amount: number;
    email: string;
    channel: string;
    paymentInstructions: string;
  };
}

export interface PaymentVerifyResponse {
  success: boolean;
  data: {
    status: 'success' | 'failed' | 'pending' | 'abandoned';
    amount: number;
    paidAt?: string;
    channel: string;
    reference: string;
    currency: string;
    customer: {
      email: string;
    };
    metadata: {
      bookingId: string;
      userId: string;
      bookingType?: string;
      adminId?: string;
    };
  };
}

class PaystackPaymentService {
  /**
   * Initialize payment with Paystack
   */
  async initializePayment(data: PaymentInitRequest): Promise<PaymentInitResponse> {
    try {
      const token = localStorage.getItem('token');


      const response = await fetch(`${getApiBaseUrl()}/api/v1/mobile/payments/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });


      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Payment API error response:', errorData);
        throw new Error(errorData.message || errorData.error || `Payment initialization failed (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error initializing payment:', error);
      throw error;
    }
  }

  /**
   * Verify payment status
   */
  async verifyPayment(reference: string): Promise<PaymentVerifyResponse> {
    try {
      const token = localStorage.getItem('token');

      // Use the verify endpoint that returns proper payment data
      const response = await fetch(`${getApiBaseUrl()}/api/v1/mobile/payments/verify/${reference}?trxref=${reference}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Payment verification failed');
      }

      const result = await response.json();
      console.log('🔍 Raw API response from /verify:', result);

      // The /verify endpoint returns the data directly in the expected format
      if (result.success && result.data) {
        return {
          success: true,
          data: {
            status: result.data.status, // Already in correct format: 'success', 'failed', 'pending'
            amount: result.data.amount || 0,
            paidAt: result.data.paidAt,
            channel: result.data.channel || 'card',
            reference: result.data.reference,
            currency: result.data.currency || 'NGN',
            customer: {
              email: result.data.customer?.email || ''
            },
            metadata: result.data.metadata || {
              bookingId: '',
              userId: '',
              bookingType: 'admin_created'
            }
          }
        };
      }

      return result;
    } catch (error) {
      console.error('Error verifying payment:', error);
      throw error;
    }
  }

  /**
   * Open Paystack checkout in a popup window
   */
  openPaymentPopup(authorizationUrl: string): Window | null {
    return window.open(
      authorizationUrl,
      'Paystack Payment',
      'width=500,height=700,left=100,top=100'
    );
  }

  /**
   * Poll payment status until completed or failed
   */
  async pollPaymentStatus(
    reference: string,
    onSuccess: (data: PaymentVerifyResponse['data']) => void,
    onFailure: (status: string) => void,
    onPending?: () => void,
    intervalMs: number = 3000,
    maxAttempts: number = 40 // 40 attempts * 3s = 2 minutes max
  ): Promise<() => void> {
    let attempts = 0;
    let intervalId: NodeJS.Timeout | null = null;

    const checkPayment = async () => {
      try {
        attempts++;

        const result = await this.verifyPayment(reference);

        if (result.data.status === 'success') {
          if (intervalId) clearInterval(intervalId);
          onSuccess(result.data);
        } else if (result.data.status === 'failed') {
          if (intervalId) clearInterval(intervalId);
          onFailure(result.data.status);
        } else if (result.data.status === 'abandoned') {
          // Don't immediately fail on abandoned - user might still be completing payment
          if (attempts >= maxAttempts) {
            if (intervalId) clearInterval(intervalId);
            onFailure(result.data.status);
          } else {
            if (onPending) onPending();
          }
        } else if (result.data.status === 'pending') {
          if (attempts >= maxAttempts) {
            if (intervalId) clearInterval(intervalId);
            onFailure('timeout');
          } else {
            if (onPending) onPending();
          }
        }
      } catch (error) {
        console.error('Error polling payment:', error);
        if (attempts >= maxAttempts) {
          if (intervalId) clearInterval(intervalId);
          onFailure('error');
        }
      }
    };

    // Check immediately first
    await checkPayment();

    // Then poll at intervals
    intervalId = setInterval(checkPayment, intervalMs);

    // Return cleanup function
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }
}

export const paystackPaymentService = new PaystackPaymentService();
